import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import ForgetPassword from '../../components/pages/password/ForgetPassword';
import AuthLogin from '../../components/pages/AuthLogin';
import Staff from '../../components/pages/staff/Staff';
import PasswordChange from '../../components/pages/password/PasswordChange';
import StaffDeleteComplete from '../../components/pages/staff/StaffDeleteComplete';
import PasswordChangeComplete from '../../components/pages/password/PasswordChangeComplete';
import PasswordResetComplete from '../../components/pages/password/PasswordResetComplete';
import StaffEditComplete from '../../components/pages/staff/StaffEditComplete';
import StaffInviteCancel from '../../components/pages/staff/StaffInviteCancel';
import StaffInviteComplete from '../../components/pages/staff/StaffInviteComplete';
import StaffInvite from '../../components/pages/staff/StaffInvite';
import StaffRegisterComplete from '../../components/pages/staff/StaffRegisterComplete';
import StaffRegister from '../../components/pages/staff/StaffRegister';
import NotFound from '../../components/pages/common/NotFound';
import { AuthProvider } from '../../contexts/auth';
import { ProxyLoginProvider } from '../../contexts/ProxyLoginContext';
import Action from '../../components/pages/common/Action';
import MfaRevert from '../../components/pages/common/MfaRevert';
import { AccessProvider } from '../../contexts/index';
import { SidebarProvider } from '../../contexts/sidebar';
import { UploadProvider } from '../../contexts/UploadContext';
import GlobalHeader from '../../components/uiParts/menu/GlobalHeader';
import ProxyModeBanner from '../../components/uiParts/menu/ProxyModeBanner';
import UploadProgressPanel from '../../components/uiParts/overlay/UploadProgressPanel';
import VersionUpdateSnackbar from '../../components/uiParts/overlay/VersionUpdateSnackbar';
import ProtectedStaffEdit from './ProtectedStaffEdit';
import ProtectedEmailImport from './ProtectedEmailImport';
import MaintenanceGate from '../../components/utils/MaintenanceGate';
import MfaEnforcementGate from '../../components/utils/MfaEnforcementGate';
import IpRestrictionGate from '../../components/utils/IpRestrictionGate';
import SamlEnforcementGate from '../../components/utils/SamlEnforcementGate';
// グループ管理関連のページコンポーネント（#2804）
import GroupList from '../../components/pages/group/GroupList';
import GroupRegister from '../../components/pages/group/GroupRegister';
// 読取パターン関連のページコンポーネント
import PatternList from '../../components/pages/pattern/PatternList';
import PatternAdd from '../../components/pages/pattern/PatternAdd';
import PatternAiOcr from '../../components/pages/pattern/PatternAiOcr';
import PatternAiOcrResult from '../../components/pages/pattern/PatternAiOcrResult';
import PatternExports from '../../components/pages/pattern/PatternExports';
import PatternAccuracyVerification from '../../components/pages/pattern/PatternAccuracyVerification';
import PatternWebhookSetting from '../../components/pages/pattern/PatternWebhookSetting';
// フリーOCR（パターンなしOCR）
import FreeAiOcr from '../../components/pages/freeAiOcr/FreeAiOcr';
import FreeAiOcrResult from '../../components/pages/freeAiOcr/FreeAiOcrResult';
import FreeAiOcrExports from '../../components/pages/freeAiOcr/FreeAiOcrExports';
import Home from '../../components/pages/home/Home';
import AuthRegister from '../../components/pages/auth/AuthRegister';
import AuthRegisterSent from '../../components/pages/auth/AuthRegisterSent';
import AuthRegisterInfo from '../../components/pages/auth/AuthRegisterInfo';
import AuthRegisterComplete from '../../components/pages/auth/AuthRegisterComplete';
import * as Sentry from '@sentry/react';
import CompanyList from '../../components/pages/admin/company/CompanyList';
import CompanyInvite from '../../components/pages/admin/company/CompanyInvite';
import CompanyInviteComplete from '../../components/pages/admin/company/CompanyInviteComplete';
import CompanyEdit from '../../components/pages/admin/company/CompanyEdit';
import CompanyEditComplete from '../../components/pages/admin/company/CompanyEditComplete';
import CompanyDetail from '../../components/pages/admin/company/CompanyDetail';
import AdminAllPatterns from '../../components/pages/admin/patterns/AdminAllPatterns';
import AdminAllMasters from '../../components/pages/admin/masters/AdminAllMasters';
import CompanyAccount from '../../components/pages/admin/company/CompanyAccount';
import OcrCreditAdd from '../../components/pages/admin/company/OcrCreditAdd';
import OcrCreditAddComplete from '../../components/pages/admin/company/OcrCreditAddComplete';
import CompanyUserLimitChange from '../../components/pages/admin/company/CompanyUserLimitChange';
import CompanyUserLimitChangeComplete from '../../components/pages/admin/company/CompanyUserLimitChangeComplete';
import CompanyPatternLimitChange from '../../components/pages/admin/company/CompanyPatternLimitChange';
import CompanyPatternLimitChangeComplete from '../../components/pages/admin/company/CompanyPatternLimitChangeComplete';
import CompanyMasterRecordLimitChange from '../../components/pages/admin/company/CompanyMasterRecordLimitChange';
import CompanyMasterRecordLimitChangeComplete from '../../components/pages/admin/company/CompanyMasterRecordLimitChangeComplete';
import CompanyPlanChange from '../../components/pages/admin/company/CompanyPlanChange';
import CompanyPlanChangeComplete from '../../components/pages/admin/company/CompanyPlanChangeComplete';
import Contact from '../../components/pages/contact/Contact';
import ContactConfirm from '../../components/pages/contact/ContactConfirm';
import ContactComplete from '../../components/pages/contact/ContactComplete';
import Master from '../../components/pages/master/Master';
import Dictionary from '../../components/pages/dictionary/Dictionary';
import ApiKeyManagementPage from '../../components/pages/companySettings/ApiKeyManagementPage';
import SecuritySettingsPage from '../../components/pages/companySettings/SecuritySettingsPage';
import EmailMessageListPage from '../../components/pages/companySettings/EmailMessageListPage';
import EmailInboxListPage from '../../components/pages/companySettings/EmailInboxListPage';
import ApiDocsPage from '../../components/pages/apiDocs/ApiDocsPage';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function RoutesIndex(): JSX.Element {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Sentry.ErrorBoundary
        fallback={<div>エラーが発生しました。ページを再読み込みしてください。</div>}
      >
        <AuthProvider>
          {/* 代理ログインの復元は認証ゲートより先に判定する（#3216）。
              ゲートの内側に置くと、MFA/SAML/IP制限の非同期確認を通る一般ユーザーでは
              Provider のマウントが遅れ、その間に ?proxy= が落ちて復元判定を失う。 */}
          <ProxyLoginProvider>
            <MaintenanceGate>
              <IpRestrictionGate>
                <MfaEnforcementGate>
                  <SamlEnforcementGate>
                    <AccessProvider>
                      <SidebarProvider>
                        <UploadProvider>
                          <ProxyModeBanner />
                          <GlobalHeader />
                          <UploadProgressPanel />
                          <VersionUpdateSnackbar />
                          <SentryRoutes>
                            <Route path="/" element={<AuthLogin />} />
                            <Route path={`/action`} element={<Action />} />
                            <Route path="/home" element={<Home />} />
                            <Route path="/staff/edit" element={<ProtectedStaffEdit />} />
                            <Route path="/ai-ocr/free" element={<FreeAiOcr />} />
                            <Route
                              path="/ai-ocr/free/result/:resultId"
                              element={<FreeAiOcrResult />}
                            />
                            <Route path="/ai-ocr/free/exports" element={<FreeAiOcrExports />} />
                            <Route path="/ai-ocr/pattern/:patternId" element={<PatternAiOcr />} />
                            <Route
                              path="/ai-ocr/pattern/:patternId/exports"
                              element={<PatternExports />}
                            />
                            <Route
                              path="/ai-ocr/pattern/:patternId/webhook"
                              element={<PatternWebhookSetting />}
                            />
                            <Route
                              path="/ai-ocr/pattern/:patternId/result/:resultId"
                              element={<PatternAiOcrResult />}
                            />
                            <Route path="/pattern" element={<PatternList />} />
                            <Route path="/pattern/add" element={<PatternAdd />} />
                            <Route
                              path="/pattern/:patternId/accuracy"
                              element={<PatternAccuracyVerification />}
                            />
                            <Route path="/pattern/:id" element={<PatternAdd />} />
                            <Route path="/auth/password_change/" element={<PasswordChange />} />
                            <Route
                              path="/auth/password_change/complete"
                              element={<PasswordChangeComplete />}
                            />
                            <Route path="/auth/mfa_revert" element={<MfaRevert />} />
                            <Route path="/auth/password_reset" element={<ForgetPassword />} />
                            <Route
                              path="/auth/password_reset/complete"
                              element={<PasswordResetComplete />}
                            />
                            <Route path="/auth/register" element={<AuthRegister />} />
                            <Route path="/auth/register/sent" element={<AuthRegisterSent />} />
                            <Route path="/auth/register/info" element={<AuthRegisterInfo />} />
                            <Route
                              path="/auth/register/complete"
                              element={<AuthRegisterComplete />}
                            />
                            <Route path="/staff/invite/cancel" element={<StaffInviteCancel />} />
                            <Route path="/staff/invite" element={<StaffInvite />} />
                            <Route
                              path="/staff/register/complete"
                              element={<StaffRegisterComplete />}
                            />
                            <Route path="/staff/invite" element={<StaffInvite />} />
                            <Route
                              path="/staff/invite/complete"
                              element={<StaffInviteComplete />}
                            />
                            <Route
                              path="/staff/delete/complete"
                              element={<StaffDeleteComplete />}
                            />
                            <Route path="/staff/edit/complete" element={<StaffEditComplete />} />
                            <Route
                              path="/staff/register/complete"
                              element={<StaffRegisterComplete />}
                            />
                            <Route path="/staff/register" element={<StaffRegister />} />
                            <Route path="/staff" element={<Staff />} />
                            <Route path="/group" element={<GroupList />} />
                            <Route path="/group/register" element={<GroupRegister />} />
                            <Route path="/group/edit" element={<GroupRegister />} />
                            <Route path="/admin/company" element={<CompanyList />} />
                            <Route path="/admin/company/:companyId" element={<CompanyDetail />} />
                            <Route path="/admin/company/invite" element={<CompanyInvite />} />
                            <Route
                              path="/admin/company/invite/complete"
                              element={<CompanyInviteComplete />}
                            />
                            <Route path="/company/edit" element={<CompanyEdit />} />
                            <Route
                              path="/company/edit/complete"
                              element={<CompanyEditComplete />}
                            />
                            <Route path="/admin/patterns" element={<AdminAllPatterns />} />
                            <Route path="/admin/masters" element={<AdminAllMasters />} />
                            <Route
                              path="/admin/ocr_credit_add/:companyId"
                              element={<OcrCreditAdd />}
                            />
                            <Route
                              path="/admin/ocr_credit_add/:companyId/complete"
                              element={<OcrCreditAddComplete />}
                            />
                            <Route
                              path="/admin/user_limit_change/:companyId"
                              element={<CompanyUserLimitChange />}
                            />
                            <Route
                              path="/admin/user_limit_change/:companyId/complete"
                              element={<CompanyUserLimitChangeComplete />}
                            />
                            <Route
                              path="/admin/pattern_limit_change/:companyId"
                              element={<CompanyPatternLimitChange />}
                            />
                            <Route
                              path="/admin/pattern_limit_change/:companyId/complete"
                              element={<CompanyPatternLimitChangeComplete />}
                            />
                            <Route
                              path="/admin/master_record_limit_change/:companyId"
                              element={<CompanyMasterRecordLimitChange />}
                            />
                            <Route
                              path="/admin/master_record_limit_change/:companyId/complete"
                              element={<CompanyMasterRecordLimitChangeComplete />}
                            />
                            <Route
                              path="/admin/plan_change/:companyId"
                              element={<CompanyPlanChange />}
                            />
                            <Route
                              path="/admin/plan_change/:companyId/complete"
                              element={<CompanyPlanChangeComplete />}
                            />
                            <Route path="/master" element={<Master />} />
                            <Route path="/dictionary" element={<Dictionary />} />
                            <Route path="/company" element={<CompanyAccount />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/contact/confirm" element={<ContactConfirm />} />
                            <Route path="/contact/complete" element={<ContactComplete />} />
                            <Route
                              path="/company-settings/api-keys"
                              element={<ApiKeyManagementPage />}
                            />
                            <Route
                              path="/company-settings/security"
                              element={<SecuritySettingsPage />}
                            />
                            <Route
                              path="/company-settings/email-messages"
                              element={
                                <ProtectedEmailImport>
                                  <EmailMessageListPage />
                                </ProtectedEmailImport>
                              }
                            />
                            <Route
                              path="/company-settings/email-inboxes"
                              element={
                                <ProtectedEmailImport>
                                  <EmailInboxListPage />
                                </ProtectedEmailImport>
                              }
                            />
                            <Route path="/api-docs" element={<ApiDocsPage />} />
                            <Route path="*" element={<NotFound />} />
                          </SentryRoutes>
                        </UploadProvider>
                      </SidebarProvider>
                    </AccessProvider>
                  </SamlEnforcementGate>
                </MfaEnforcementGate>
              </IpRestrictionGate>
            </MaintenanceGate>
          </ProxyLoginProvider>
        </AuthProvider>
      </Sentry.ErrorBoundary>
    </BrowserRouter>
  );
}

export default RoutesIndex;
